import {
  buildComparePath,
  normalizeCompareIds,
  parseCompareIds,
} from '~/utils/compare'

export function useCompare() {
  const route = useRoute()
  const router = useRouter()
  const cartStore = useCartStore()
  const shopPath = useShopPath()

  function withShop(pathWithOptionalQuery: string) {
    return shopPath(pathWithOptionalQuery)
  }

  function isComparePage(path = route.path) {
    return path === shopPath('/compara') || path.endsWith('/compara')
  }

  /** On /compara the selection lives in the URL; elsewhere it lives in the cart store (floating bar). */
  const compareIds = computed(() => {
    if (isComparePage()) {
      return parseCompareIds(route.query.ids)
    }
    return cartStore.compareProducts
  })

  const comparePath = computed(() => withShop(buildComparePath(cartStore.compareProducts)))

  function setCompareIds(ids: number[]) {
    const next = normalizeCompareIds(ids)
    if (isComparePage()) {
      router.replace(withShop(buildComparePath(next)))
      return
    }
    cartStore.setCompareProducts(next)
  }

  /**
   * Commit the current selection into the URL and clear the floating-bar selection
   * so the bar does not reappear on other pages after visiting /compara.
   */
  async function initCompareRoute() {
    if (!isComparePage()) return

    const fromQuery = parseCompareIds(route.query.ids)
    if (fromQuery.length) {
      cartStore.clearCompare()
      return
    }

    if (cartStore.compareProducts.length) {
      const path = withShop(buildComparePath(cartStore.compareProducts))
      await router.replace(path)
      cartStore.clearCompare()
    }
  }

  function removeCompareId(productId: number) {
    setCompareIds(compareIds.value.filter((id) => id !== productId))
  }

  function clearCompareSelection() {
    if (isComparePage()) {
      cartStore.clearCompare()
      router.replace(shopPath('/compara'))
      return
    }
    cartStore.clearCompare()
  }

  return {
    compareIds,
    comparePath,
    setCompareIds,
    initCompareRoute,
    removeCompareId,
    clearCompareSelection,
    buildComparePath: (ids: number[]) => withShop(buildComparePath(ids)),
  }
}
